import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { TFilters, TOrderBy } from 'redux/types';
import { TPgListRequest, TPgListResponse } from 'services/api/paymentGateway/types';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { ItemWithId, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { notificationContainer } from 'utils/notificationContainer';
import { usePgListLoader } from './usePgListLoader';

type TPgListProps<T extends ItemWithId> = {
	title: string;
	/** Full, stable structure of the table (module constant). */
	structure: TTableStructure<keyof T, T>[];
	/** Stable function (an `api.paymentGateway.*` method). */
	fetcher: (request: TPgListRequest) => Promise<TPgListResponse<T>>;
	/** Called for a row button; `action` is the button `name`. */
	onAction?: (row: T, action: string) => void;
	/** Must be memoized. */
	extraFilters?: TFilters;
	defaultOrder?: TOrderBy;
	/** Changing the value re-reads the list (after an action). */
	reloadKey?: number;
	/** Controls next to the title. */
	headerContent?: ReactNode;
	errorText: string;
};

/**
 * List of the admin API (`POST …/list`, 03 §0): table with the standard filter panel, sorting and
 * paging in the URL; the request is rebuilt on every URL change.
 */
export const PgList = <T extends ItemWithId>({
	title,
	structure,
	fetcher,
	onAction,
	extraFilters,
	defaultOrder,
	reloadKey = 0,
	headerContent,
	errorText,
}: TPgListProps<T>) => {
	const [items, setItems] = useState<TPgListResponse<T> | null>(null);
	const [loading, setLoading] = useState(false);
	const [tableStructure, setTableStructure] = useState(structure);
	const lastRequest = useRef<TPgListRequest | null>(null);
	const sequence = useRef(0);
	const mounted = useRef(true);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// `reloadKey` is part of the identity: a new value makes the loader call `load` again.
	const load = useCallback(
		(request: TPgListRequest) => {
			lastRequest.current = request;
			sequence.current += 1;
			const mine = sequence.current;
			setLoading(true);
			fetcher(request)
				.then((response) => {
					if (mounted.current && mine === sequence.current) setItems(response);
				})
				.catch((error) => {
					if (mounted.current && mine === sequence.current) {
						notificationContainer(getPgErrorMessage(error, errorText), 'error');
					}
				})
				.finally(() => {
					if (mounted.current && mine === sequence.current) setLoading(false);
				});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fetcher, errorText, reloadKey],
	);
	usePgListLoader(structure, load, { extraFilters, defaultOrder });

	const handleAction = useCallback(
		(id: number, action: string) => {
			const row = items?.data.find((item) => item.id === id);
			if (row && onAction) onAction(row, action);
		},
		[items, onAction],
	);

	return (
		<EasTableWithFilter<T>
			tableStructure={tableStructure}
			items={items}
			itemsIsLoading={loading}
			handleAction={handleAction}
			onTableStructureChange={setTableStructure}
			skeletonRows={Array.from(new Array(5))}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 1,
					flexWrap: 'wrap',
				}}
			>
				<Typography variant="body1" color="primary" fontWeight="bold">
					{title}
				</Typography>
				<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
					{headerContent}
					<Button
						size="small"
						onClick={() => lastRequest.current && load(lastRequest.current)}
						disabled={loading}
						startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
					>
						Refresh
					</Button>
				</Box>
			</Box>
		</EasTableWithFilter>
	);
};
